import { useEffect, useMemo, useRef, useState } from 'react';
import { airportCountryLookup, tripCountries } from '@/utils/travelRequirements';

// Loaded once per page; after that the hook answers synchronously, so the
// checkout never flickers between "add travelers" and payment.
let airportLookup = null;
let airportLookupLoading = null;
function loadAirportLookup() {
  if (!airportLookupLoading) {
    airportLookupLoading = Promise.all([import('@/data/searchAirports'), import('@/data/hiddenAirportCountries')])
      .then(([{ SEARCH_AIRPORTS }, { HIDDEN_AIRPORT_COUNTRIES }]) => {
        airportLookup = airportCountryLookup(SEARCH_AIRPORTS, HIDDEN_AIRPORT_COUNTRIES);
        return airportLookup;
      })
      .catch((error) => {
        airportLookupLoading = null;
        throw error;
      });
  }
  return airportLookupLoading;
}

let visaTable = null;
let visaTableLoading = null;
function loadVisaTable() {
  if (!visaTableLoading) {
    visaTableLoading = import('@/data/visaRequirements')
      .then(({ VISA_TABLE }) => {
        visaTable = VISA_TABLE;
        return visaTable;
      })
      .catch((error) => {
        visaTableLoading = null;
        throw error;
      });
  }
  return visaTableLoading;
}

/** What the result depends on: each segment's airports and dates. Callers
 *  often rebuild the flight object every render, so its identity can't be. */
function flightKey(flight) {
  const parts = [];
  for (const slice of flight?.slices || []) {
    for (const segment of slice?.segments || []) {
      parts.push([
        segment?.origin?.iata_code || segment?.origin || '',
        segment?.destination?.iata_code || segment?.destination || '',
        segment?.departing_at || '',
        segment?.arriving_at || '',
      ].join('|'));
    }
    parts.push('/');
  }
  return parts.join(',');
}

const NOT_READY = { ready: false, international: false, stops: [], startCountry: null, lastTravelDate: null };

function safeTripCountries(flight, countryOf) {
  try {
    return { ready: true, ...tripCountries(flight, countryOf) };
  } catch {
    // Unreadable flight data: never leave checkout waiting on this.
    return { ready: true, international: false, stops: [], startCountry: null, lastTravelDate: null };
  }
}

/**
 * The countries a flight touches and, for international trips, the visa
 * table — both lazy-loaded so domestic checkouts never download the visa data.
 *
 * Returns { ready, international, stops, startCountry, lastTravelDate, visaTable }.
 * `ready` turns true as soon as the trip's countries are known (the airport
 * list); the visa table can arrive a moment later and only adds notes, so
 * payment never waits on it. Review keeps payment closed while `ready` is false.
 */
export default function useTripTravelRequirements(flight) {
  const flightRef = useRef(flight);
  flightRef.current = flight;
  const key = flightKey(flight);
  const hasSlices = Boolean(flight?.slices?.length);

  // Bumped when a lazy load finishes, so the memos below recompute.
  const [loads, setLoads] = useState(0);

  const trip = useMemo(() => {
    if (!hasSlices || !airportLookup) return NOT_READY;
    return safeTripCountries(flightRef.current, airportLookup);
    // `loads` stands in for the module-level airport list arriving.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, hasSlices, loads]);

  useEffect(() => {
    let cancelled = false;
    const bump = () => { if (!cancelled) setLoads((n) => n + 1); };
    if (!hasSlices) return undefined;
    if (!airportLookup) {
      loadAirportLookup().then(bump).catch((error) => {
        // The airport list didn't load (offline, bad deploy): fall back to the
        // airline's own country codes on the segments, with no visa table.
        console.warn('Travel requirements unavailable:', error?.message || error);
        airportLookup = () => null;
        bump();
      });
    } else if (trip.international && !visaTable) {
      loadVisaTable().then(bump).catch((error) => console.warn('Visa data unavailable:', error?.message || error));
    }
    return () => {
      cancelled = true;
    };
  }, [key, hasSlices, trip.international]);

  return useMemo(
    () => ({ ...trip, visaTable: trip.international ? visaTable : null }),
    // `loads` stands in for the module-level visa table arriving.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trip, loads],
  );
}
